import { CapacitorUpdater } from '@capgo/capacitor-updater'
import '@splidejs/splide/dist/css/themes/splide-default.min.css'
import { DefaultSeo } from 'next-seo'
import App from 'next/app'
import Head from 'next/head'
import { withRouter } from 'next/router'
import 'react-dates/lib/css/_datepicker.css'
import { SkeletonTheme } from 'react-loading-skeleton'
import { Provider } from 'react-redux'
import styled from 'styled-components'
import { Normalize } from 'styled-normalize'
import { BaseModalBackground, ModalProvider } from 'styled-react-modal'
// import 'bootstrap/dist/css/bootstrap.min.css'
import { EdcProvider } from 'context/edcContext'
import { PostHogProvider } from 'providers/posthogProvider'
import { SplashScreen } from '@capacitor/splash-screen'
import { StatusBar } from '@capacitor/status-bar'

import Modal from 'components/Atoms/Modal'
import UsercentricsLoader from 'components/Atoms/UsercentricsLoader'
import AppComponent from 'components/Molecules/AppComponent'
import { routerEventsListening } from 'helpers/router'
import { sendPageSize } from 'helpers/sdk'
import withApplication from 'hocs/withApplication'
import withApollo from 'hocs/withApollo'
import withIntercom from 'hocs/withIntercom'
import withMultiLanguage from 'hocs/withMultiLanguage'
import { appWithTranslation } from 'i18n-web/i18next'
import { fetchCountries } from 'redux-web/utils/countries/actions'
import {
  getAccessToken,
  removeConversionId,
} from 'redux-web/utils/session/actions'
import {
  fetchSettings,
  setDisableHeaderAndFooter,
  setParentDomain,
} from 'redux-web/utils/settings/actions'
import { fetchMe, fetchOriginalUser } from 'redux-web/utils/user/actions'
import withReduxStore from 'redux-web/with-redux-store'
import GlobalStyles from 'styles/GlobalStyles'
import { CapacitorBrowserCheck } from 'components/Molecules/CapacitorBrowserCheck'

import SEO from '../../next-seo.config'
import { getImpersonateUserId } from '../redux-web/utils/session/actions'

CapacitorUpdater.notifyAppReady()

const ModalBackground = styled(BaseModalBackground)`
  height: -webkit-fill-available;
  background-color: rgba(55, 56, 64, 0.9);
  z-index: 110;
`

class MyApp extends App {
  constructor(props) {
    super(props)
    this.state = {
      statusBarHeight: 0,
    }
  }

  handleStatusBar() {
    if (StatusBar) {
      StatusBar.setStyle({
        style: 'DARK',
      }).catch(() => {})

      StatusBar.setOverlaysWebView({
        overlay: true,
      }).catch(() => {})

      StatusBar.setBackgroundColor({
        color: '#373840',
      }).catch(() => {})
    }
  }

  componentDidUpdate() {
    this.handleStatusBar()
  }

  componentDidMount() {
    const hideSplashScreen = async () => {
      await SplashScreen.hide()
    }

    hideSplashScreen()

    removeConversionId()
    const router = this.props?.router
    const { dispatch } = this.props?.reduxStore

    const {
      application,
      parentDomain,
      disableHeaderAndFooter,
      sdk,
      emailSupport,
      phoneNumberSupport,
    } = router?.query

    dispatch(
      fetchSettings({
        application,
        sdk,
        support: { emailSupport, phoneNumberSupport },
      })
    )

    dispatch(fetchCountries(sdk))

    if (getAccessToken()) {
      dispatch(fetchMe(sdk))
    }

    if (getImpersonateUserId()) {
      dispatch(fetchOriginalUser(sdk))
    }

    if (parentDomain) {
      dispatch(setParentDomain(parentDomain))
    }

    if (disableHeaderAndFooter) {
      dispatch(setDisableHeaderAndFooter(disableHeaderAndFooter))
    }

    if (sdk) {
      sendPageSize({
        parentDomain: parentDomain,
      })
    }

    this.handleStatusBar()

    // listening router status.
    routerEventsListening(this, parentDomain)
  }

  render() {
    const { Component, pageProps, reduxStore, err } = this.props

    return (
      <PostHogProvider>
        <Head>
          <meta
            name="viewport"
            content="width=device-width, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"
          />
          <link
            rel="apple-touch-icon"
            sizes="57x57"
            href="/static/favicons/apple-touch-icon-57x57.png"
          />
          <link
            rel="apple-touch-icon"
            sizes="60x60"
            href="/static/favicons/apple-touch-icon-60x60.png"
          />
          <link
            rel="apple-touch-icon"
            sizes="72x72"
            href="/static/favicons/apple-touch-icon-72x72.png"
          />
          <link
            rel="apple-touch-icon"
            sizes="76x76"
            href="/static/favicons/apple-touch-icon-76x76.png"
          />
          <link
            rel="apple-touch-icon"
            sizes="114x114"
            href="/static/favicons/apple-touch-icon-114x114.png"
          />
          <link
            rel="apple-touch-icon"
            sizes="120x120"
            href="/static/favicons/apple-touch-icon-120x120.png"
          />
          <link
            rel="apple-touch-icon"
            sizes="144x144"
            href="/static/favicons/apple-touch-icon-144x144.png"
          />
          <link
            rel="apple-touch-icon"
            sizes="152x152"
            href="/static/favicons/apple-touch-icon-152x152.png"
          />
          <link
            rel="apple-touch-icon"
            sizes="180x180"
            href="/static/favicons/apple-touch-icon-180x180.png"
          />
          <link
            rel="icon"
            type="image/png"
            href="/static/favicons/favicon-32x32.png"
            sizes="32x32"
          />
          <link
            rel="icon"
            type="image/png"
            href="/static/favicons/android-chrome-192x192.png"
            sizes="192x192"
          />
          <link
            rel="icon"
            type="image/png"
            href="/static/favicons/favicon-96x96.png"
            sizes="96x96"
          />
          <link
            rel="icon"
            type="image/png"
            href="/static/favicons/favicon-16x16.png"
            sizes="16x16"
          />
          <link
            rel="mask-icon"
            href="/static/favicons/safari-pinned-tab.svg"
            color="#f5b928"
          />
          <link rel="shortcut icon" href="/static/favicons/favicon.ico" />
        </Head>
        {pageProps?.canonicalURL && (
          <DefaultSeo {...SEO} canonical={pageProps?.canonicalURL} />
        )}
        <Normalize />
        <GlobalStyles statusBarHeight={this.state.statusBarHeight || 0} />
        <UsercentricsLoader />
        <Provider store={reduxStore}>
          <EdcProvider>
            <ModalProvider backgroundComponent={ModalBackground}>
              <SkeletonTheme color="#e8e8e8" highlightColor="#f0f0f0">
                <CapacitorBrowserCheck />
                <AppComponent
                  Component={Component}
                  pageProps={pageProps}
                  err={err}
                  loadingPage={this.state?.isLoading}
                  router={this.props.router}
                  intercom={this.props.intercom}
                  application={this.props.application}
                />
              </SkeletonTheme>
              <Modal />
            </ModalProvider>
          </EdcProvider>
        </Provider>
      </PostHogProvider>
    )
  }
}

export default withReduxStore(
  withRouter(
    withApollo(
      withIntercom(
        withMultiLanguage(appWithTranslation(withApplication(MyApp)))
      )
    )
  )
)
